import closeIcon from "@assets/images/close-thin.svg";
import zipIcon from "@assets/images/zip.svg";
import { FormControlLabel, Grid, Radio, RadioGroup } from "@material-ui/core";
import MyTypography from "@saleor/components/MyTypography";
import SingleSelectField from "@saleor/components/SingleSelectField";
import classNames from "classnames";
import { FormikErrors, getIn } from "formik";
import React from "react";
import SVG from "react-inlinesvg";
import { FormattedMessage } from "react-intl";

import {
  DocumentType,
  DocumentTypeLabel,
  IDocumentFile,
  PaymentMethod,
  PaymentMethodLabel
} from "../../types";
import useStyle from "./styles";
export interface DocumentListProps {
  items: IDocumentFile[];
  errors: string | string[] | Array<FormikErrors<IDocumentFile>>;
  isEditable?: boolean;
  onRemove: (index: number) => void;
  onChangeDocumentType: (
    index: number,
    type: IDocumentFile["documentType"]
  ) => void;
  onChangePaymentMethod: (
    index: number,
    type: IDocumentFile["paymentMethod"]
  ) => void;
}

const DocumentList = ({
  items,
  errors,
  isEditable = false,
  onRemove,
  onChangeDocumentType,
  onChangePaymentMethod
}: DocumentListProps) => {
  const classes = useStyle();

  const DOCUMENT_TYPE_OPTIONS = [
    {
      label: DocumentTypeLabel.PAYIN,
      value: DocumentType.PAYIN
    },
    {
      label: DocumentTypeLabel.WITHHOLD,
      value: DocumentType.WITHHOLD
    },
    {
      label: DocumentTypeLabel.OTHER,
      value: DocumentType.OTHER
    }
  ];

  const PAYMENT_METHOD_OPTIONS = [
    {
      label: PaymentMethodLabel.BANK_TRANSFER,
      value: PaymentMethod.BANK_TRANSFER
    },
    {
      label: PaymentMethodLabel.CREDIT_CARD,
      value: PaymentMethod.CREDIT_CARD
    }
  ];

  return (
    <Grid
      container
      spacing={2}
      className={classes.root}
      data-testid="cashsale-document-list"
    >
      <Grid item container spacing={2}>
        <Grid item xs={4}>
          <MyTypography variant="subtitle3">
            <FormattedMessage defaultMessage="File Name" />
          </MyTypography>
        </Grid>
        <Grid item xs={3}>
          <MyTypography variant="subtitle3">
            <FormattedMessage defaultMessage="Document Type" />
          </MyTypography>
        </Grid>
        <Grid item xs={5}>
          <MyTypography variant="subtitle3">
            <FormattedMessage defaultMessage="Payment Method" />
          </MyTypography>
        </Grid>
      </Grid>

      {items.map((item, index) => (
        <Grid
          key={`${index}-${item.file?.name}`}
          item
          container
          spacing={2}
          className={classNames(!isEditable && classes.fileLineReadOnly)}
          data-testid="document-row"
        >
          <Grid item xs={4}>
            <div
              className={classNames(classes.filename, {
                [classes.filenameEditable]: isEditable,
                [classes.filenameError]: isEditable && !!errors?.[index]
              })}
            >
              <SVG src={zipIcon} className={classes.fileIcon} />
              <div className={classes.fileTitle} title={item.file?.name}>
                {item.file?.name}
              </div>
              {isEditable && (
                <SVG
                  data-testid="closeIcon-Document"
                  src={closeIcon}
                  className={classes.closeIcon}
                  onClick={() => onRemove(index)}
                />
              )}
            </div>
            {!!errors?.[index] && (
              <div className={classes.fileErrorTex}>
                {getIn(errors, `${index}`)}
              </div>
            )}
          </Grid>
          <Grid item xs={3}>
            {isEditable ? (
              <SingleSelectField
                name="documentType"
                choices={DOCUMENT_TYPE_OPTIONS}
                value={item.documentType}
                onChange={e => onChangeDocumentType(index, e.target.value)}
                className={classes.selectField}
                testId="documentType-select"
                InputProps={{
                  inputProps: { "data-testid": "documentType-input" }
                }}
              />
            ) : (
              <MyTypography variant={"body3"}>
                {DocumentTypeLabel[item.documentType]}
              </MyTypography>
            )}
          </Grid>
          <Grid item xs={5}>
            {item.documentType === DocumentType.PAYIN &&
              (isEditable ? (
                <RadioGroup
                  aria-labelledby="demo-controlled-radio-buttons-group"
                  name="paymentMethod"
                  value={item.paymentMethod}
                  onChange={e =>
                    onChangePaymentMethod(
                      index,
                      e.target.value as PaymentMethod
                    )
                  }
                  className={classes.radioGroup}
                  data-testid="paymentMethod-radio-groups"
                >
                  {PAYMENT_METHOD_OPTIONS.map((option, optionKey) => (
                    <FormControlLabel
                      key={`radio-${optionKey}`}
                      data-testid={`radio-${optionKey}`}
                      value={option.value}
                      control={<Radio />}
                      label={
                        <MyTypography variant={"body3"}>
                          {option.label}
                        </MyTypography>
                      }
                    />
                  ))}
                </RadioGroup>
              ) : (
                <MyTypography variant={"body3"}>
                  {PaymentMethodLabel[item.paymentMethod]}
                </MyTypography>
              ))}
          </Grid>
        </Grid>
      ))}
    </Grid>
  );
};

export default DocumentList;
